// packages/scoring-types/src/prism-types.ts
// FRAME — PRISM (Personal Recognition & Intelligent Scoring Memory)
// Adaptive personalized scoring that learns the user's aesthetic preferences,
// recognizes the owner, and adjusts scores based on behavioral signals.
//
// PRIVACY ARCHITECTURE:
//   Transient Zone: Face detection, feature comparison — in-memory only, never persisted.
//   Persistent Zone: Only scalar floats, category labels, boolean flags, preference weights.
//   NO face embeddings, landmarks, bounding boxes, or raw pixel data ever cross
//   from transient to persistent storage. This keeps FRAME compliant with
//   BIPA (per Barnett v. Apple), CCPA, GDPR Article 9, and Apple Guideline 5.1.2.

// ─── Per-Photo Aesthetic Properties ────────────────────────────
// Captured at score time from already-computed signals. Zero new ML compute.

export interface PrismAestheticProperties {
  // From existing scoring pipeline
  portraitScore: number | null;
  lightingScore: number;
  compositionScore: number;
  sceneScore: number;
  compositeScore: number;

  // Style signals (extracted from existing pipeline intermediates)
  colorWarmth: number;            // R/B ratio mapped to 0-1 (0=cool, 1=warm)
  colorSaturation: number;        // Average saturation 0-1
  contrastLevel: number;          // Histogram stdDev mapped to 0-1
  cropTightness: number;          // Face-to-frame ratio (0 if no face)
  backgroundComplexity: number;   // Edge density outside subject bbox, 0-1
  ruleOfThirdsScore: number;      // Subject alignment with power points, 0-1
  symmetryScore: number;          // Left-right visual weight balance, 0-1

  // Expression signals (if face present, from MediaPipe blendshapes)
  smileIntensity: number;         // mouthSmileL+R avg → 0-1
  eyeContactScore: number;        // Gaze consistency → 0-1
  expressionNaturalness: number;  // Duchenne vs forced → 0-1

  // Context
  isOwnerPresent: boolean;
  faceCount: number;
  isSelfie: boolean;
}

// ─── Behavioral Signals ────────────────────────────────────────

export type PrismBehavioralEventType =
  | 'viewed_detail'     // User tapped to see full diagnostic card
  | 'viewed_duration'   // Time spent on detail view (value = seconds)
  | 'favorited'         // User tapped favorite/heart
  | 'unfavorited'       // User removed favorite
  | 'shared'            // User shared photo (strongest positive signal)
  | 'deleted'           // User removed from results (strongest negative)
  | 'saved_edit'        // User applied an edit suggestion
  | 'rejected_edit'     // User dismissed an edit suggestion
  | 'score_disagreed'   // User explicitly disagreed with score (thumbs down)
  | 'swipe_past';       // User scrolled past quickly (<0.5s visible)

export interface PrismBehavioralEvent {
  photoId: string;
  sessionId: string;
  timestamp: string;        // ISO 8601
  eventType: PrismBehavioralEventType;
  value?: number;           // e.g. duration in seconds for viewed_duration
}

/** Weight of each behavioral signal for preference learning. Positive = user likes it, negative = dislikes. */
export const BEHAVIORAL_SIGNAL_WEIGHTS: Record<PrismBehavioralEventType, number> = {
  shared: 1.0,              // Strongest positive — trusts this photo enough to show others
  favorited: 0.8,           // Strong positive
  saved_edit: 0.7,          // Deep engagement, accepted FRAME's suggestion
  viewed_duration: 0.5,     // Only counted if > 10s
  viewed_detail: 0.3,       // Engaged viewing
  swipe_past: -0.3,         // Mild negative
  rejected_edit: -0.4,      // Disagreed with suggestion
  unfavorited: -0.6,        // Changed mind
  score_disagreed: -0.8,    // Explicit disagreement
  deleted: -1.0,            // Strongest negative
};

// ─── Preference Vector (Style DNA) ─────────────────────────────

export interface PrismPreferenceVector {
  // Dimensional affinities: how much the user values each dimension
  portraitAffinity: number;       // -1 to 1
  lightingAffinity: number;       // -1 to 1
  compositionAffinity: number;    // -1 to 1
  sceneAffinity: number;          // -1 to 1

  // Style preferences
  warmthPreference: number;       // -1 (cool) to 1 (warm)
  contrastPreference: number;     // -1 (soft) to 1 (punchy)
  saturationPreference: number;   // -1 (muted) to 1 (vibrant)
  cropPreference: number;         // -1 (wide) to 1 (tight crop)
  backgroundPreference: number;   // -1 (busy/contextual) to 1 (clean/minimal)

  // Expression preferences (for portrait-heavy modes)
  smilePreference: number;        // -1 (serious) to 1 (big smile)
  eyeContactPreference: number;   // -1 (candid) to 1 (direct gaze)
  poseNaturalness: number;        // -1 (posed) to 1 (candid/natural)

  // Meta
  confidence: number;             // 0-1: how much data backs this vector
  photosSampled: number;          // Total photos that contributed
  lastUpdated: string;            // ISO 8601
}

export const DEFAULT_PREFERENCE_VECTOR: PrismPreferenceVector = {
  portraitAffinity: 0, lightingAffinity: 0, compositionAffinity: 0, sceneAffinity: 0,
  warmthPreference: 0, contrastPreference: 0, saturationPreference: 0,
  cropPreference: 0, backgroundPreference: 0,
  smilePreference: 0, eyeContactPreference: 0, poseNaturalness: 0,
  confidence: 0, photosSampled: 0, lastUpdated: '',
};

// ─── Owner Recognition ─────────────────────────────────────────

export interface PrismOwnerEnrollment {
  enrolled: boolean;
  enrolledAt: string | null;       // ISO 8601
  referencePhotoIds: string[];     // Apple Photos asset IDs only (max 10) — NO face data
  selfieSessionCount: number;      // Sessions containing selfies (enrollment trigger at 3)
}

export const DEFAULT_OWNER_ENROLLMENT: PrismOwnerEnrollment = {
  enrolled: false, enrolledAt: null, referencePhotoIds: [], selfieSessionCount: 0,
};

export interface PrismOwnerBaseline {
  avgPortraitComposition: number;  // Rolling EMA of owner's portrait scores
  avgLighting: number;
  avgComposition: number;
  bestPortraitScore: number;       // Owner's personal best
  bestCompositeScore: number;
  photoCount: number;
  lastUpdated: string;             // ISO 8601
}

export const DEFAULT_OWNER_BASELINE: PrismOwnerBaseline = {
  avgPortraitComposition: 0, avgLighting: 0, avgComposition: 0,
  bestPortraitScore: 0, bestCompositeScore: 0, photoCount: 0, lastUpdated: '',
};

// ─── PRISM Score Modifier ──────────────────────────────────────

export interface PrismScoreModifier {
  personalizedComposite: number;   // Final score after PRISM adjustment
  baseComposite: number;           // Original objective score (always preserved)
  prismDelta: number;              // How much PRISM changed the score
  adjustmentReasons: string[];     // Human-readable reasons
  ownerBoost: number;              // Boost from owner-specific baseline comparison
  preferenceBoost: number;         // Boost from style/preference alignment
  confidence: number;              // How confident PRISM is (mirrors vector confidence)
}

// ─── Progressive Confidence Ramp ───────────────────────────────
// PRISM has minimal impact early and increases influence as confidence grows.

export interface PrismConfidenceLevel {
  minConfidence: number;
  maxInfluence: number;   // Maximum ± score adjustment at this level
  label: string;
}

export const PRISM_CONFIDENCE_RAMP: PrismConfidenceLevel[] = [
  { minConfidence: 0.0, maxInfluence: 0,   label: 'Learning' },
  { minConfidence: 0.1, maxInfluence: 0.3, label: 'Getting to know you' },
  { minConfidence: 0.3, maxInfluence: 0.7, label: 'Tuning in' },
  { minConfidence: 0.5, maxInfluence: 1.0, label: 'Personalized' },
  { minConfidence: 0.8, maxInfluence: 1.5, label: 'Deeply personalized' },
];

// ─── PRISM Store Schema (for on-device persistence) ────────────

export interface PrismStoreData {
  enrollment: PrismOwnerEnrollment;
  preferenceVector: PrismPreferenceVector;
  ownerBaseline: PrismOwnerBaseline | null;
  behavioralEvents: PrismBehavioralEvent[];   // 90-day rolling window
  photoAesthetics: Record<string, PrismAestheticProperties>; // keyed by photoId
  enabled: boolean;
  paused: boolean;              // Paused = apply existing prefs but don't learn new ones
  lastPruneDate: string;
}

export const DEFAULT_PRISM_STORE: PrismStoreData = {
  enrollment: DEFAULT_OWNER_ENROLLMENT,
  preferenceVector: DEFAULT_PREFERENCE_VECTOR,
  ownerBaseline: null,
  behavioralEvents: [],
  photoAesthetics: {},
  enabled: false,
  paused: false,
  lastPruneDate: new Date().toISOString(),
};
